import { inr, num, fmtDT } from "@/lib/format";
import DataTable from "./DataTable";
import { Kpi, Section } from "./kit";

const ITEM_COLS = [
  { key: "name", label: "Product" },
  { key: "opening", label: "Opening", type: "int" },
  { key: "issued", label: "Issued", type: "int" },
  { key: "transfer_in", label: "Tr. In", type: "int" },
  { key: "transfer_out", label: "Tr. Out", type: "int" },
  { key: "returned", label: "Returned", type: "int" },
  { key: "loss", label: "Loss", type: "int" },
  { key: "available", label: "Available", type: "int" },
  { key: "closing", label: "Closing", type: "int" },
  { key: "sold", label: "Sold", type: "int" },
  { key: "price", label: "Price", type: "money" },
  { key: "sales_value", label: "Sales Value", type: "money" },
];

export function ItemsTable({ day, title, exportable }) {
  return (
    <DataTable columns={ITEM_COLS} rows={day.items} searchable={false} exportable={exportable} pageSize={0}
      totals={["opening", "issued", "transfer_in", "transfer_out", "returned", "loss", "available", "closing", "sold", "sales_value"]}
      title={title || `Day ${day.date}`} testid="day-items-table" />
  );
}

export function ReconPanel({ day }) {
  const rows = [
    ["Sales Value", day.totals.sales_value, "recon-sales-value"],
    ["(−) GPay / UPI", day.gpay, "recon-gpay"],
    ["(−) Expenses", day.expenses_total, "recon-expenses"],
    ["= Expected Cash", day.expected_cash, "recon-expected-cash", true],
    ["Actual Cash", day.cash, "recon-actual-cash"],
    ["Difference", day.difference, "recon-difference"],
  ];
  return (
    <div className="space-y-1.5" data-testid="recon-panel">
      {rows.map(([l, v, t, b]) => (
        <div key={t} className={`flex justify-between text-sm ${b ? "border-t border-slate-200 pt-1.5 font-semibold" : ""}`}>
          <span className="text-slate-600">{l}</span>
          <span className="font-mono tabular" data-testid={t}>{inr(v)}</span>
        </div>
      ))}
      <div className={`mt-2 flex justify-between rounded px-3 py-2 text-sm font-bold ${day.receivable > 0 ? "bg-red-50 text-red-700" : day.surplus > 0 ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
        <span>{day.receivable > 0 ? "Salesman Receivable" : day.surplus > 0 ? "Cash Surplus / Overage" : "Balanced"}</span>
        <span className="font-mono" data-testid="recon-result">{inr(day.receivable > 0 ? day.receivable : day.surplus)}</span>
      </div>
    </div>
  );
}

export function HistoryList({ history }) {
  if (!history?.length) return null;
  return (
    <Section title="Reopen History" testid="reopen-history">
      <ul className="space-y-3">
        {history.map((h, i) => (
          <li key={i} className="rounded border border-orange-200 bg-orange-50/50 p-3 text-sm">
            <p className="font-semibold text-orange-800">Reopened by {h.by_name || h.by} · {fmtDT(h.at)}</p>
            <p className="text-slate-700">Reason: {h.reason}</p>
            {h.previous && (
              <p className="mt-1 font-mono text-xs text-slate-500">
                Previous: {num(h.previous.totals?.packs_sold)} pks · {inr(h.previous.totals?.sales_value)} · Cash {inr(h.previous.cash)} · GPay {inr(h.previous.gpay)} · Receivable {inr(h.previous.receivable)}
              </p>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

export default function DayView({ day }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Packs Sold" value={num(day.totals.packs_sold)} tone="blue" testid="day-kpi-packs" />
        <Kpi label="Sales Value" value={inr(day.totals.sales_value)} tone="green" testid="day-kpi-sales" />
        <Kpi label="Expenses" value={inr(day.expenses_total)} tone="amber" testid="day-kpi-expenses" />
        <Kpi label={day.receivable > 0 ? "Receivable" : "Surplus"} value={inr(day.receivable > 0 ? day.receivable : day.surplus)} tone={day.receivable > 0 ? "red" : "default"} testid="day-kpi-receivable" />
      </div>
      <Section title="Stock & Sales"><ItemsTable day={day} exportable /></Section>
      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Cash Reconciliation"><ReconPanel day={day} /></Section>
        <HistoryList history={day.history} />
      </div>
      {day.submitted_at && <p className="text-xs text-slate-500">Submitted by {day.submitted_by_name || day.submitted_by} on {fmtDT(day.submitted_at)}</p>}
    </div>
  );
}
